import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import HomePage from './pages/HomePage'; // eager — landing/LCP page, no extra chunk hop
import { CartProvider } from './context/CartContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { WishlistProvider } from './context/WishlistContext';
import './App.css';

// Every other route is code-split so the first load doesn't ship the admin panel,
// the TipTap editor, or pages the visitor isn't on. Admin in particular is heavy.
const ShopPage = lazy(() => import('./pages/ShopPage'));
const PreorderPacksPage = lazy(() => import('./pages/PreorderPacksPage'));
const AdvisorPage = lazy(() => import('./pages/AdvisorPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const VegetableDetailPage = lazy(() => import('./pages/VegetableDetailPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const OurFarmersPage = lazy(() => import('./pages/OurFarmersPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const WinnersPage = lazy(() => import('./pages/WinnersPage'));

const RouteFallback = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Spin size="large" />
  </div>
);

// Shopper-only shell: the cart/wishlist/customer-auth contexts live here so they
// mount for storefront routes but NOT for /admin. Keeping them off /admin stops
// the admin panel from fetching a signed-in shopper's cart (/api/cart) and
// wishlist (/api/wishlist/ids) — the admin panel has its own auth and uses neither.
const CustomerLayout = () => (
  <CustomerAuthProvider>
    <WishlistProvider>
      <CartProvider>
        <Outlet />
      </CartProvider>
    </WishlistProvider>
  </CustomerAuthProvider>
);

const theme = {
  token: {
    colorPrimary: '#2E7D32',
    colorSuccess: '#66BB6A',
    colorWarning: '#FF9800',
    borderRadius: 12,
    fontFamily: "'Poppins', sans-serif",
  },
  components: {
    Button: { borderRadius: 8, controlHeight: 44 },
    Card: { borderRadius: 16 },
  },
};

function App() {
  // Warm the Shop chunk during idle time — most visitors go Home → Shop, so this
  // makes that navigation instant without adding anything to the critical path.
  useEffect(() => {
    const prefetch = () => { import('./pages/ShopPage'); };
    const w = window as any;
    if ('requestIdleCallback' in w) {
      const id = w.requestIdleCallback(prefetch, { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(prefetch, 2000);
    return () => clearTimeout(t);
  }, []);

  return (
    <ConfigProvider theme={theme}>
      <Router>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Admin panel — outside the shopper contexts (own auth, no cart/wishlist). */}
            <Route path="/admin" element={<AdminPage />} />
            {/* Storefront routes — wrapped in the shopper contexts. */}
            <Route element={<CustomerLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />

              <Route path="/health-packs" element={<PreorderPacksPage />} />
              <Route path="/advisor" element={<AdvisorPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/vegetable/:id" element={<VegetableDetailPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/our-farmers" element={<OurFarmersPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/winners" element={<WinnersPage />} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </ConfigProvider>
  );
}

export default App;
